// useNotifications.js - Custom hook for notifications
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useNavigate } from 'react-router-dom'; 

export const useNotifications = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch notifications from the database
  const fetchNotifications = useCallback(async () => {
    if (!user || !currentTenant) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const { data, error } = await supabase.rpc('get_user_notifications', {
        p_user_id: user.id,
        p_tenant_id: currentTenant.id,
        p_limit: 50,
        p_unread_only: false
      });

      if (error) throw error;

      setNotifications(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user, currentTenant]);

  // Mark notifications as read
  const markAsRead = useCallback(async (notificationIds) => {
    if (!user || !currentTenant) return;

    try {
      const ids = Array.isArray(notificationIds) ? notificationIds : [notificationIds];
      
      const { error } = await supabase.rpc('mark_notifications_read', {
        p_user_id: user.id,
        p_tenant_id: currentTenant.id,
        p_notification_ids: ids
      });

      if (error) throw error;

      // Update local state
      setNotifications(prev => prev.map(notif => 
        ids.includes(notif.id) ? { ...notif, is_read: true } : notif
      ));
    } catch (err) {
      console.error('Error marking notifications as read:', err);
    }
  }, [user, currentTenant]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!user || !currentTenant) return;

    try {
      const { error } = await supabase.rpc('mark_notifications_read', {
        p_user_id: user.id,
        p_tenant_id: currentTenant.id,
        p_notification_ids: null // null means mark all as read
      });

      if (error) throw error;

      // Update local state
      setNotifications(prev => prev.map(notif => ({ ...notif, is_read: true })));
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, [user, currentTenant]);

  // Delete/dismiss notification
  const deleteNotification = useCallback(async (notificationId) => {
    if (!user || !currentTenant) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_dismissed: true })
        .eq('id', notificationId)
        .eq('user_id', user.id)
        .eq('tenant_id', currentTenant.id);

      if (error) throw error;

      // Remove from local state
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  }, [user, currentTenant]);

  // Create a new notification (for testing or manual creation)
  const createNotification = useCallback(async (notificationData) => {
    if (!user || !currentTenant) return;

    try {
      const { data, error } = await supabase.rpc('create_notification', {
        p_user_id: notificationData.user_id || user.id,
        p_tenant_id: currentTenant.id,
        p_type: notificationData.type,
        p_title: notificationData.title,
        p_message: notificationData.message,
        p_priority: notificationData.priority || 'medium',
        p_action_data: notificationData.action_data || {},
        p_related_time_entry_id: notificationData.related_time_entry_id,
        p_related_project_id: notificationData.related_project_id,
        p_related_client_id: notificationData.related_client_id,
        p_expires_at: notificationData.expires_at
      });

      if (error) throw error;

      // Refresh notifications
      fetchNotifications();
      
      return data;
    } catch (err) {
      console.error('Error creating notification:', err);
      throw err;
    }
  }, [user, currentTenant, fetchNotifications]);

  // Set up real-time subscription
  useEffect(() => {
    if (!user || !currentTenant) return;

    fetchNotifications();

    // Subscribe to real-time notifications
    const subscription = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('New notification received:', payload);
          // Add new notification to state
          setNotifications(prev => [payload.new, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Notification updated:', payload);
          // Update notification in state
          setNotifications(prev => prev.map(notif => 
            notif.id === payload.new.id ? payload.new : notif
          ));
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user, currentTenant, fetchNotifications]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    createNotification,
    refetch: fetchNotifications
  };
};

// NotificationHelpers.js - Helper functions for creating notifications
export const NotificationHelpers = {
  // Create timesheet reminder notifications
  createTimesheetReminder: async (userId, tenantId, unsubmittedCount, weekEnding, totalHours) => {
    const { data, error } = await supabase.rpc('create_notification', {
      p_user_id: userId,
      p_tenant_id: tenantId,
      p_type: 'timesheet_reminder',
      p_title: `You have ${unsubmittedCount} unsubmitted timesheet${unsubmittedCount !== 1 ? 's' : ''} from last week`,
      p_message: `Week ending ${weekEnding} - Submit before deadline`,
      p_priority: unsubmittedCount > 5 ? 'high' : 'medium',
      p_action_data: {
        weekEnding,
        entryCount: unsubmittedCount,
        totalHours
      },
      p_expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 1 week
    });

    if (error) throw error;
    return data;
  },

  // Create approval pending notifications for managers
  createApprovalPendingNotification: async (managerId, tenantId, pendingEntries) => {
    const userNames = [...new Set(pendingEntries.map(entry => entry.user_name))];
    const totalHours = pendingEntries.reduce((sum, entry) => sum + parseFloat(entry.duration_hours), 0);
    const oldestEntry = Math.min(...pendingEntries.map(entry => new Date(entry.submitted_at).getTime()));

    const { data, error } = await supabase.rpc('create_notification', {
      p_user_id: managerId,
      p_tenant_id: tenantId,
      p_type: 'approval_pending',
      p_title: `${pendingEntries.length} entries pending your approval`,
      p_message: 'Team members are waiting for timesheet approval',
      p_priority: pendingEntries.length > 10 ? 'high' : 'medium',
      p_action_data: {
        pendingCount: pendingEntries.length,
        users: userNames.slice(0, 3), // Show first 3 users
        totalHours,
        oldestEntry: new Date(oldestEntry).toISOString().split('T')[0]
      }
    });

    if (error) throw error;
    return data;
  },

  // Create invoice ready notification
  createInvoiceReadyNotification: async (userId, tenantId, clientName, amount, billableHours, period) => {
    const { data, error } = await supabase.rpc('create_notification', {
      p_user_id: userId,
      p_tenant_id: tenantId,
      p_type: 'invoice_ready',
      p_title: `Invoice ready for ${clientName}`,
      p_message: `$${amount.toLocaleString()} - ${billableHours} billable hours this ${period}`,
      p_priority: 'medium',
      p_action_data: {
        clientName,
        amount,
        billableHours,
        period
      }
    });

    if (error) throw error;
    return data;
  },

  // Create deadline warning notification
  createDeadlineWarningNotification: async (userId, tenantId, projectName, daysLeft, projectId, completion, hoursRemaining) => {
    const { data, error } = await supabase.rpc('create_notification', {
      p_user_id: userId,
      p_tenant_id: tenantId,
      p_type: 'deadline_warning',
      p_title: 'Project deadline approaching',
      p_message: `${projectName} due in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`,
      p_priority: daysLeft <= 3 ? 'high' : 'medium',
      p_action_data: {
        projectName,
        daysLeft,
        completion,
        hoursRemaining
      },
      p_related_project_id: projectId
    });

    if (error) throw error;
    return data;
  },

  // Create team update notification
  createTeamUpdateNotification: async (tenantId, userName, role, teamName, actionType = 'joined') => {
    // Get all users in the tenant to notify
    const { data: tenantUsers, error: usersError } = await supabase
      .from('tenant_memberships')
      .select('user_id')
      .eq('tenant_id', tenantId)
      .eq('status', 'active');

    if (usersError) throw usersError;

    // Create notification for each user
    const notifications = await Promise.all(
      tenantUsers.map(async ({ user_id }) => {
        const { data, error } = await supabase.rpc('create_notification', {
          p_user_id: user_id,
          p_tenant_id: tenantId,
          p_type: 'team_update',
          p_title: `${userName} ${actionType} your team`,
          p_message: `New team member added to ${teamName}`,
          p_priority: 'low',
          p_action_data: {
            userName,
            role,
            teamName,
            actionType
          }
        });

        if (error) throw error;
        return data;
      })
    );

    return notifications;
  },

  // Create overtime alert notification
  createOvertimeAlertNotification: async (managerId, tenantId, userName, hoursWorked, normalHours = 40) => {
    const overtimeHours = hoursWorked - normalHours;

    const { data, error } = await supabase.rpc('create_notification', {
      p_user_id: managerId,
      p_tenant_id: tenantId,
      p_type: 'overtime_alert',
      p_title: `${userName} worked ${hoursWorked} hours this week`,
      p_message: 'Consider reviewing workload distribution',
      p_priority: overtimeHours > 20 ? 'high' : 'medium',
      p_action_data: {
        userName,
        hoursWorked,
        normalHours,
        overtimeHours
      }
    });

    if (error) throw error;
    return data;
  }
};

// NotificationActions.js - Action handlers for notifications
export const useNotificationActions = () => {
  const { currentTenant } = useTenant();
  const { user } = useAuth(); // Add this line to fix the error

  const handleAction = useCallback(async (actionType, notification) => {
    console.log(`Handling action: ${actionType}`, notification);

    switch (actionType) {
      case 'submit_timesheet':
        // Navigate to timesheet submission page with date filter
        if (notification.action_data?.weekEnding) {
          const weekStart = new Date(notification.action_data.weekEnding);
          weekStart.setDate(weekStart.getDate() - 6);
          
          // You can use your router here
          // navigate('/time-entries', { 
          //   state: { 
          //     filterStartDate: weekStart.toISOString().split('T')[0],
          //     filterEndDate: notification.action_data.weekEnding,
          //     filterStatus: 'draft'
          //   }
          // });
          
          console.log('Navigate to time entries with draft filter');
        }
        break;

      case 'view_entries':
        // Navigate to time entries list
        console.log('Navigate to time entries');
        break;

      case 'review_entries':
      case 'review_all':
        // Navigate to approval queue
        console.log('Navigate to approval queue');
        break;

      case 'bulk_approve':
        // Perform bulk approval
        try {
          if (!user || !currentTenant) {
            console.error('User or tenant not available for bulk approval');
            return;
          }

          const { error } = await supabase
            .from('time_entries')
            .update({ 
              status: 'approved',
              approved_at: new Date().toISOString(),
              approved_by: user.id
            })
            .eq('tenant_id', currentTenant.id)
            .eq('status', 'submitted');

          if (error) throw error;
          
          console.log('Bulk approval completed');
          // Show success message
          // You might want to refresh data or show a toast
        } catch (error) {
          console.error('Error in bulk approval:', error);
        }
        break;

      case 'generate_invoice':
        // Navigate to invoice generation
        if (notification.action_data?.clientName) {
          console.log('Generate invoice for:', notification.action_data.clientName);
          // navigate('/invoices/generate', { 
          //   state: { 
          //     clientName: notification.action_data.clientName 
          //   }
          // });
        }
        break;

      case 'view_details':
        // Navigate to detailed view
        console.log('View details for notification');
        break;

      case 'view_project':
        // Navigate to project details
        if (notification.related_project_id) {
          console.log('Navigate to project:', notification.related_project_id);
          // navigate(`/projects/${notification.related_project_id}`);
        }
        break;

      case 'add_time':
      case 'log_time':
        // Navigate to time entry creation with project pre-selected
        if (notification.related_project_id) {
          console.log('Create time entry for project:', notification.related_project_id);
          // navigate('/time-entries/new', { 
          //   state: { 
          //     preselectedProject: notification.related_project_id 
          //   }
          // });
        }
        break;

      case 'view_profile':
        // Navigate to user profile
        console.log('View profile');
        break;

      case 'review_schedule':
        // Navigate to schedule/calendar view
        console.log('Review schedule');
        break;

      case 'contact_user':
        // Open communication with user
        console.log('Contact user:', notification.action_data?.userName);
        break;

      default:
        console.log(`Unhandled action: ${actionType}`);
    }
  }, [currentTenant]);

  return { handleAction };
};

// Integration with your existing EntriesView component
export const useNotificationTriggers = () => {
  const { user } = useAuth();
  const { currentTenant, userRole } = useTenant();

  // Trigger approval pending notifications when entries are submitted
  const checkPendingApprovals = useCallback(async () => {
    if (!currentTenant || userRole !== 'admin' && userRole !== 'manager') return;

    try {
      const { data: pendingEntries, error } = await supabase
        .from('time_entries_with_roles')
        .select('*')
        .eq('tenant_id', currentTenant.id)
        .eq('status', 'submitted')
        .lt('submitted_at', new Date(Date.now() - 60 * 60 * 1000).toISOString()); // Older than 1 hour

      if (error) throw error;

      if (pendingEntries && pendingEntries.length > 0) {
        await NotificationHelpers.createApprovalPendingNotification(
          user.id,
          currentTenant.id,
          pendingEntries
        );
      }
    } catch (error) {
      console.error('Error checking pending approvals:', error);
    }
  }, [user, currentTenant, userRole]);

  // Check for unsubmitted timesheets
  const checkUnsubmittedTimesheets = useCallback(async () => {
    if (!user || !currentTenant) return;

    try {
      const lastWeekEnd = new Date();
      lastWeekEnd.setDate(lastWeekEnd.getDate() - lastWeekEnd.getDay()); // Last Sunday
      const lastWeekStart = new Date(lastWeekEnd);
      lastWeekStart.setDate(lastWeekStart.getDate() - 6); // Last Monday

      const { data: unsubmittedEntries, error } = await supabase
        .from('time_entries')
        .select('duration_hours')
        .eq('user_id', user.id)
        .eq('tenant_id', currentTenant.id)
        .eq('status', 'draft')
        .gte('entry_date', lastWeekStart.toISOString().split('T')[0])
        .lte('entry_date', lastWeekEnd.toISOString().split('T')[0]);

      if (error) throw error;

      if (unsubmittedEntries && unsubmittedEntries.length > 0) {
        const totalHours = unsubmittedEntries.reduce((sum, entry) => sum + parseFloat(entry.duration_hours), 0);
        
        await NotificationHelpers.createTimesheetReminder(
          user.id,
          currentTenant.id,
          unsubmittedEntries.length,
          lastWeekEnd.toISOString().split('T')[0],
          totalHours
        );
      }
    } catch (error) {
      console.error('Error checking unsubmitted timesheets:', error);
    }
  }, [user, currentTenant]);

  return {
    checkPendingApprovals,
    checkUnsubmittedTimesheets
  };
};